import './ComposeShoutoutContent.scss';

/* eslint-disable @typescript-eslint/no-shadow */
import React, {
  memo,
  useState,
} from 'react';

import {
  Button,
  Input,
} from 'antd';
import { PiImage } from 'react-icons/pi';

import EmojiPickerModel
  from '../../../../../components/EmojiPickerModel/EmojiPickerModel';
import {
  uploadFiles,
  uploadFilesHandler,
} from '../../../../../shared/file';
import {
  ITQShoutoutCategory,
  TQShoutoutCategory,
} from '../../../../../types/tqConnectTypes';
import {
  ICreateTQShoutoutRequest,
} from '../../../../../types/tqConnectTypes/user';
import { TQ_VALIDATION } from '../../../../TQConnect/constants';

interface Props {
  formValues: ICreateTQShoutoutRequest;
  setFormValues: React.Dispatch<React.SetStateAction<ICreateTQShoutoutRequest>>;
  setMode: (
    mode:
      | 'select-category'
      | 'compose'
      | 'input-description'
      | 'review-shoutout',
  ) => void;
  selectedShoutoutCategory: ITQShoutoutCategory | null;
  onOpenDescriptionEditor: () => void;
}

const ComposeShoutoutContent: React.FC<Props> = ({
  formValues,
  setFormValues,
  setMode,
  selectedShoutoutCategory,
  onOpenDescriptionEditor,
}) => {
  const [showCustomIconPicker, setShowCustomIconPicker] = useState(false);
  const uploadCoverImage = () =>
    uploadFilesHandler({
      type: 'image',
      accept: ['jpg', 'jpeg', 'png', 'gif'],
      onUpload: (files, type) => {
        uploadFiles({
          files,
          type,
          maxSizeUnit: 'MB',
          maxSize: TQ_VALIDATION.MAX_SHOUTOUT_COVER_IMAGE_SIZE / (1024 * 1024),
          uploadToServer: true,
          uploadCallback: (_, urls) => {
            if (urls?.[0]) {
              setFormValues((prev) => ({ ...prev, coverImage: urls[0] }));
            }
          },
        });
      },
    });

  const handleBack = () => {
    setMode('select-category');
  };

  const isCustomCategory = formValues.category?.id === 'custom';
  const isNextDisabled = isCustomCategory
    ? !formValues.description?.trim() ||
      !formValues.category?.name?.trim() ||
      formValues.category.name.trim().length < 3
    : !formValues.description?.trim();

  const onCategoryNameChange = (name: string) => {
    if (formValues.category) {
      setFormValues((prev) => ({
        ...prev,
        category: prev.category ? { ...prev.category, name } : null,
      }));
    }
  };

  const handleNext = () => {
    if (!isNextDisabled) {
      setMode('review-shoutout');
    }
  };

  const onSelectCustomIcon = () => {
    if (formValues.category?.id === TQShoutoutCategory.CUSTOM) {
      setShowCustomIconPicker(true);
    }
  };

  const onSelectCustomCategoryEmoji = (emoji: string) => {
    if (formValues.category) {
      setFormValues((prev) => ({
        ...prev,
        category: prev.category ? { ...prev.category, logo: emoji } : null,
      }));
    }
    setShowCustomIconPicker(false);
  };

  return (
    <div className="compose-shoutout-content">
      <div className="compose-shoutout-content__body">
        <div className="compose-shoutout-content__banner">
          <img
            src={formValues.coverImage}
            alt="Shoutout cover"
            className="compose-shoutout-content__banner__image"
          />
          <button
            type="button"
            className="compose-shoutout-content__banner__change-image"
            onClick={uploadCoverImage}
            aria-label="Change cover image">
            <PiImage size={24} />
          </button>
          {formValues.category && (
            <div className="compose-shoutout-content__banner__badge">
              <span
                className="compose-shoutout-content__banner__badge__logo"
                onClick={onSelectCustomIcon}>
                {formValues.category.logo}
              </span>
              <span className="compose-shoutout-content__banner__badge__name">
                {formValues.category.name}
              </span>
            </div>
          )}
        </div>

        {isCustomCategory && (
          <div className="compose-shoutout-content__custom-name">
            <h3 className="compose-shoutout-content__heading">
              Give your custom category a name
            </h3>
            <Input
              className="compose-shoutout-content__custom-name__input"
              placeholder="e.g., 'Client Review Meeting'"
              value={formValues.category?.name ?? ''}
              onChange={(e) => onCategoryNameChange(e.target.value)}
              maxLength={TQ_VALIDATION.MAX_SHOUTOUT_CATEGORY_TITLE_LENGTH}
              showCount
            />
          </div>
        )}

        <h3 className="compose-shoutout-content__heading">
          {isCustomCategory
            ? 'Brief Description'
            : (selectedShoutoutCategory?.label ?? 'What did they achieve?')}
        </h3>

        <div
          className="compose-shoutout-content__description-trigger"
          onClick={onOpenDescriptionEditor}>
          {formValues.description?.trim() ? (
            <span className="compose-shoutout-content__description-trigger__value">
              {formValues.description}
            </span>
          ) : (
            <span className="compose-shoutout-content__description-trigger__placeholder">
              {selectedShoutoutCategory?.placeholder ??
                'Share what they accomplished and how it reflects their journey.'}
            </span>
          )}
        </div>
      </div>

      <div className="compose-shoutout-content__footer">
        <Button
          className="siteBtn grayBtn outlineBtn compose-shoutout-content__footer__back"
          onClick={handleBack}>
          Back
        </Button>
        <Button
          className="siteBtn accentBtnV1 compose-shoutout-content__footer__next"
          onClick={handleNext}
          disabled={isNextDisabled}>
          Next
        </Button>
      </div>

      <EmojiPickerModel
        show={showCustomIconPicker}
        closeModal={() => setShowCustomIconPicker(false)}
        onSelectEmoji={onSelectCustomCategoryEmoji}
      />
    </div>
  );
};

export default memo(ComposeShoutoutContent);
